import { observable, makeObservable, flow, action, computed } from 'mobx';
import { getWellFormViewAndItsDirectories, updateWell, createWell, deleteGeoTasks } from 'src/api/new-well/requests';
import { mapData } from 'src/api/new-well/serializers/common-serializers';
import { serializeFormForRequest } from 'src/api/new-well/serializers/serialize-form-for-request';
import { TIdTriple, TFormRaw, TCreateUpdateWellReturnType } from 'src/api/new-well/types';
import { BaseApiError, Validation400ApiError, ValidationError } from 'src/errors';
import { ConflictResolvingError } from 'src/errors/conflicts';
import { AddWellToChartSidebarStore } from 'src/features/add-well-to-chart-sidebar/add-well-to-chart-sidebar.store';
import { FormStore } from 'src/features/well-form/entities/form.entity';
import { FormPlugin } from 'src/features/well-form/plugins';
import { RegularComboBox } from 'src/shared/entities/control-entities';
import { assert } from 'src/shared/utils/assert';
import { hasValue } from 'src/shared/utils/common';
import { getRandomNumber } from 'src/shared/utils/get-random-number';
import { getInitialTripleIdsByWell } from 'src/shared/utils/get-triple-ids';
import { parsePrimitivesFromString } from 'src/shared/utils/parse-primitives-from-string';
import { ENABLE_EDITING_VARIANTS } from 'src/store/editing/types';

import { Directories } from '../directories/directories.store';
import { DraftsStore } from '../drafts/drafts-store';
import { EditingStore } from '../editing/editing-store';
import { I18NextStore } from '../i18next/i18next-store';
import { NotificationsStore } from '../notifications-store/notifications-store';
import { PlanVersionStore } from '../plan-version';
import { RootStore } from '../root-store';

type TCreateFormOptions = {
  additionalPlugins?: FormPlugin[];
  well?: { well: Record<string, unknown>; setBy: 'attrName' | 'fieldId' };
};

/** This class uses for form managment on Wells, Planning and Graph pages */
export class WellFormManager {
  readonly directories: Directories;
  readonly notifications: NotificationsStore;
  readonly i18: I18NextStore;
  readonly planVersion: PlanVersionStore;
  readonly editing: EditingStore;
  readonly drafts: DraftsStore;
  readonly formPlugins: FormPlugin[];
  readonly addWellToChartStore: AddWellToChartSidebarStore;
  readonly instanceId: number;
  protected readonly rootStore: RootStore;
  private deletedGeoTasks: number[] = [];

  @observable currentTripleIds: TIdTriple[] | null = null;
  @observable isFormLoading = false;
  @observable isFormOpen = false;
  @observable isAddWellToChartSidebarOpened = false;
  @observable currentFormStore: FormStore | null = null;
  @observable formList: FormStore[] = [];
  @observable tripleIdsList: Record<number, TIdTriple[]> = {};
  @observable formView?: TFormRaw;
  @observable isSaveUpdateLoading = false;
  failedForms = observable.map<FormStore, Error>();
  formsDisposers = observable.map<FormStore, VoidFunction>();

  onCloseForm?(): void | Promise<void>;
  onFormSaveSuccess?(): void | Promise<void>;
  onFormCancel?(): void | Promise<void>;
  onFormInit?(): void | Promise<void>;
  onCreateWell?(well: TCreateUpdateWellReturnType): void | Promise<void>;
  onUpdateWell?(well: TCreateUpdateWellReturnType): void | Promise<void>;

  constructor(rootStore: RootStore, formPlugins: FormPlugin[] = []) {
    this.directories = rootStore.directories;
    this.notifications = rootStore.notifications;
    this.i18 = rootStore.i18;
    this.planVersion = rootStore.planVersion;
    this.editing = rootStore.editing;
    this.drafts = rootStore.drafts;
    this.formPlugins = formPlugins;
    this.rootStore = rootStore;
    this.addWellToChartStore = new AddWellToChartSidebarStore({
      draftStore: rootStore.drafts,
      directories: rootStore.directories,
      appSettings: rootStore.appSettings,
      notifications: rootStore.notifications,
      wellFormManager: this,
      view: rootStore.views.addWellToChart,
    });
    this.instanceId = getRandomNumber();

    makeObservable(this);
  }

  @action.bound
  private updatePlanWellTripleIds(updatedFormId: number, tripleIds: TIdTriple[]) {
    const newTrilepleIdsObject: Record<number, TIdTriple[]> = { [updatedFormId]: tripleIds };

    const newWellPlacementIdObject = tripleIds.find((triples) => triples.objectType === 'GOplan_WellPlacement');

    if (!newWellPlacementIdObject) {
      throw new Error('cannot find WellPlacementIdObject');
    }

    for (const stringifiedFormId in this.tripleIdsList) {
      const formId = Number(stringifiedFormId);

      const newTripleIds = [
        ...(this.tripleIdsList[formId]?.filter((o) => o.objectType !== 'GOplan_WellPlacement') ?? []),
        newWellPlacementIdObject,
      ];

      newTrilepleIdsObject[formId] = newTripleIds;
    }

    this.tripleIdsList = newTrilepleIdsObject;
  }

  @flow.bound
  private async *loadFormView() {
    try {
      const formView = await getWellFormViewAndItsDirectories(this.directories);
      yield;

      this.formView = formView;
    } catch (e) {
      yield;

      console.error(e);
      if (e instanceof BaseApiError && e.responseMessage) {
        this.notifications.showErrorMessage(e.responseMessage);
        return;
      }
      this.notifications.showErrorMessageT('newWellForm:Errors.failedLoadForm');
    }
  }

  async init(): Promise<void> {
    await this.loadFormView();
    await this.onFormInit?.();
  }

  @computed
  get currentWellName(): string {
    if (!this.currentFormStore?.isEditingMode) {
      return this.i18.t('newWellForm:Subheader.title');
    }

    const wellControl = this.currentFormStore?.fields['wellName'];
    if (wellControl instanceof RegularComboBox) {
      const wellDirValue = this.directories
        .getObject(wellControl.refObjectType)
        ?.find((dirValue) => dirValue.id === wellControl.value);

      if (wellControl.refObjectAttr) {
        const wellName = wellDirValue?.data[wellControl.refObjectAttr];
        return wellName?.toString() || '';
      }
    }

    return '';
  }

  @flow.bound
  protected async *validateForm(form: FormStore) {
    if (form.validationManager.validationRequest) {
      const isValidationPassed = await form.validationManager.validationRequest;
      yield;

      form.validationManager.validationRequest = null;

      if (!isValidationPassed) {
        throw new Error('');
      }
    }

    form.validationManager.validateForm();
  }

  @flow.bound
  async *changeForm(formId: number) {
    if (!this.currentFormStore) {
      return;
    }

    assert(this.currentFormStore, 'currentFormStore is not presented');

    try {
      const newCurrentForm = this.formList.find((form) => form.id === formId);
      const newTripleIds = this.tripleIdsList[formId];

      if (newCurrentForm && newTripleIds.length) {
        this.currentFormStore = newCurrentForm;
        this.currentTripleIds = newTripleIds;
      }
    } catch (e) {
      yield;
      if (e instanceof ValidationError) {
        this.notifications.showErrorMessage(e.message);
      }
    }
  }

  @action.bound
  setIsFormOpen(is: boolean) {
    this.isFormOpen = is;
  }

  @action.bound
  createForm(options?: TCreateFormOptions): FormStore | null {
    if (!this.formView) {
      return null;
    }

    const savedWell = options?.well ?? null;
    const additionalPlugins = options?.additionalPlugins ?? [];

    const newForm = mapData(this.rootStore, savedWell?.well ?? null, [...this.formPlugins, ...additionalPlugins]);
    this.currentFormStore = newForm;
    this.formList.push(newForm);

    const newFormDisposer = newForm.init(this.formView, savedWell?.well ?? null);

    if (savedWell) {
      const newTripleIds = getInitialTripleIdsByWell(savedWell.well);
      this.tripleIdsList[newForm.id] = newTripleIds;
      this.currentTripleIds = newTripleIds;
    }

    if (newFormDisposer) {
      this.formsDisposers.set(newForm, newFormDisposer);
    }

    return newForm;
  }

  @flow.bound
  async *onWellAdd() {
    if (!this.formView) {
      this.notifications.showErrorMessageT('newWellForm:Errors.failedOpenEditForm');
      return;
    }

    this.isFormLoading = true;
    this.isFormOpen = true;
    try {
      if (!this.editing.isEditing) {
        const enableEditingVariant = await this.editing.enableEditing();
        yield;

        if (enableEditingVariant === ENABLE_EDITING_VARIANTS.cancel) {
          return;
        }
      }
      this.currentFormStore = this.createForm();
    } catch (e) {
      yield;
      console.error(e);
      this.isFormOpen = false;
    } finally {
      this.isFormLoading = false;
    }
  }

  // TODO: временно отключить эту возможность
  // @action.bound
  // splitApproach({ sectionId, stage, approach }: TSplitApproachOptions) {
  //   if (this.currentFormStore && this.failedForms.has(this.currentFormStore)) {
  //     return;
  //   }

  //   const approachesTab = this.currentFormStore?.tabs.find((tab): tab is ApproachesTab => tab instanceof ApproachesTab);
  //   if (!approachesTab) {
  //     return;
  //   }
  //   if (!approachesTab.approachesList.approachReference) return;
  //   const splitSectionIndex = stage.sectionsList.sections.findIndex((section) => section.id === sectionId);
  //   const clonedApproach = approach.clone();
  //   if (!clonedApproach) return;
  //   const clonedStage = stage.clone();
  //   const splicedSections = stage.sectionsList.sections.splice(splitSectionIndex + 1);
  //   clonedStage.sectionsList.setSections(splicedSections);

  //   clonedApproach.stagesList.setStages([clonedStage]);
  //   const serializedApproach = serializeApproach(clonedApproach);

  //   const approachReference = approachesTab.approachesList.approachReference;
  //   const approachControl = mapApproach(
  //     { approachReference, directories: this.directories, eventBus: approachesTab.eventBus },
  //     serializedApproach
  //   );
  //   if (approachControl) {
  //     approachesTab.eventBus.publish('newRigOperation', approachControl);
  //     approachesTab.approachesList.approaches.push(approachControl);
  //   }
  // }

  protected async updateWell(form: FormStore, tripleIds: TIdTriple[]): Promise<TCreateUpdateWellReturnType> {
    await this.validateForm(form);

    assert(this.drafts.draftVersionId, 'draftVersionId is invalid');
    const serializedForm = serializeFormForRequest(form, tripleIds);

    const well = await updateWell(serializedForm, this.drafts.draftVersionId, tripleIds);

    const withoutPreloader = true;
    const isSucceed = await this.drafts.conflictResolver.resolveConflict(
      well.conflictRigOperation,
      this.drafts.draftVersionId,
      withoutPreloader,
    );

    if (!isSucceed) {
      throw new ConflictResolvingError();
    }

    await this.onUpdateWell?.(well);

    return well;
  }

  private async createWell(form: FormStore, tripleIds: TIdTriple[]): Promise<TCreateUpdateWellReturnType> {
    assert(this.drafts.draftVersionId, 'draftVersionId is invalid');
    const serializedForm = serializeFormForRequest(form, tripleIds);

    const well = await createWell(serializedForm, this.drafts.draftVersionId);

    const withoutPreloader = true;
    const isSucceed = await this.drafts.conflictResolver.resolveConflict(
      well.conflictRigOperation,
      this.drafts.draftVersionId,
      withoutPreloader,
    );

    if (!isSucceed) {
      throw new ConflictResolvingError();
    }

    await this.onCreateWell?.(well);

    return well;
  }

  @flow.bound
  async *saveGeologicalTasks() {
    assert(this.drafts.draftVersionId, 'draftVersionId is invalid');

    if (!this.formList.length) {
      return;
    }

    this.isSaveUpdateLoading = true;

    try {
      await Promise.all(this.formList.map((form) => this.validateForm(form)));

      for (const formInstance of this.formList) {
        this.failedForms.delete(formInstance);
        const formTrippleIds = this.tripleIdsList[formInstance.id];
        const isGeologicalTaskIdPresented = !!formTrippleIds?.find((ids) => ids.objectType === 'GOplan_GeologicalTask');

        try {
          const well = await (isGeologicalTaskIdPresented
            ? this.updateWell(formInstance, formTrippleIds)
            : this.createWell(formInstance, formTrippleIds));
          yield;

          this.updatePlanWellTripleIds(formInstance.id, well.idList);
          formInstance.setRigOperationsIds(well.idList);
        } catch (e) {
          const error = e instanceof Error ? e : new Error('failed to create or update well');
          this.failedForms.set(formInstance, error);
          throw e;
        }
      }

      if (this.deletedGeoTasks.length) {
        await deleteGeoTasks(this.drafts.draftVersionId, this.deletedGeoTasks);
      }

      await this.onFormSaveSuccess?.();
      yield;

      await this.onCloseForm?.();
      yield;
    } catch (e) {
      yield;

      if ((e instanceof Validation400ApiError || e instanceof ValidationError) && e.message) {
        this.notifications.showErrorMessage(e.message);
        return;
      } else if (e instanceof ConflictResolvingError) {
        this.notifications.showErrorMessageT('errors:failedToResolveConflict');
        return;
      } else if (e instanceof BaseApiError && e.responseMessage) {
        this.notifications.showErrorMessage(e.responseMessage);
        return;
      } else if (this.currentTripleIds) {
        this.notifications.showErrorMessageT('newWellForm:Errors.updateWell');
      } else {
        this.notifications.showErrorMessageT('newWellForm:Errors.createWell');
      }

      console.error(e);
    } finally {
      this.isSaveUpdateLoading = false;
    }
  }

  @action.bound
  deleteGeoTask(wellInstId: number) {
    const { formInst, formIndex } = this.formList.reduce(
      (acc: { formIndex: number | null; formInst: FormStore | null }, form, index) => {
        if (form.id === wellInstId) {
          acc = { formIndex: index, formInst: form };
        }

        return acc;
      },
      { formIndex: null, formInst: null },
    );
    const geoTaskData = formInst?.initialFormData;

    if (!formInst || !geoTaskData || !hasValue(formIndex)) {
      console.error('cannot find corresponding geoTaskData');
      return;
    }

    const geoTaskId = (() => {
      const id = parsePrimitivesFromString(geoTaskData['GOplan_GeologicalTask.id']);

      if (!hasValue(id) || typeof id !== 'number') {
        return null;
      }

      return id;
    })();

    if (hasValue(geoTaskId)) {
      this.deletedGeoTasks.push(geoTaskId);
    }

    const copiedFormList = [...this.formList];
    copiedFormList.splice(formIndex, 1);

    const copiedTripleIdsList = { ...this.tripleIdsList };
    delete copiedTripleIdsList[wellInstId];

    this.tripleIdsList = copiedTripleIdsList;

    const prevWellForm = copiedFormList.at(-1);

    if (!prevWellForm) {
      console.error('cannot find prev wellForm');
    } else {
      this.changeForm(prevWellForm.id);
    }

    this.formList = copiedFormList;
  }

  @action.bound
  disposeForms(): void {
    for (const disposer of this.formsDisposers.values()) {
      disposer();
    }

    this.formsDisposers.clear();
  }

  @action.bound
  resetFormManager() {
    this.currentTripleIds = null;
    this.tripleIdsList = {};
    this.formList = [];
    this.currentFormStore = null;
    this.failedForms.clear();
    this.deletedGeoTasks = [];
    this.disposeForms();
  }

  @action.bound
  cancelForm() {
    this.isFormOpen = false;
    this.resetFormManager();
    this.onFormCancel?.();
    this.onCloseForm?.();
  }
}
